import type { CellContext } from '@tanstack/react-table'
import type { SyntheticEvent } from 'react'

import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { IconHorizontalMoreOutline } from '@icons/IconHorizontalMoreOutline'
import { RowActionV2 } from '../DataTableV2/components/RowActionV2'
import type { DataTableV2Action, DataTableV2DataRow } from '../DataTableV2/types'
import type { OrgChartRecord } from './types'

interface OrgChartCardActionsProps {
  actions: DataTableV2Action[]
  record: OrgChartRecord
  title: string
}

function stopEventPropagation(event: SyntheticEvent) {
  event.stopPropagation()
}

function actionInfoForRecord(record: OrgChartRecord): CellContext<DataTableV2DataRow, unknown> {
  return {
    row: {
      original: record as DataTableV2DataRow,
    },
  } as unknown as CellContext<DataTableV2DataRow, unknown>
}

export function OrgChartCardActions({ actions, record, title }: OrgChartCardActionsProps) {
  const visibleActions = actions.filter((action) => action.isVisible)
  if (visibleActions.length === 0) {
    return null
  }

  const inlineActions = visibleActions.filter((action) => action.inDropdownMenu === false)
  const dropdownActions = visibleActions.filter((action) => action.inDropdownMenu !== false)
  const info = actionInfoForRecord(record)

  return (
    <div
      className="absolute right-2 top-2 z-10 flex items-center gap-1"
      onClick={stopEventPropagation}
      onKeyDown={stopEventPropagation}
      onPointerDown={stopEventPropagation}
    >
      {inlineActions.map((action) => (
        <RowActionV2 key={action.id} action={action} info={info} displayAs="icon-button" />
      ))}
      {dropdownActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label={`Open actions for ${title}`}
            >
              <IconHorizontalMoreOutline size={16} />
              <span className="sr-only">Open actions for {title}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            onClick={stopEventPropagation}
            onKeyDown={stopEventPropagation}
            onPointerDown={stopEventPropagation}
          >
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {dropdownActions.map((action) => (
              <RowActionV2 key={action.id} action={action} info={info} displayAs="dropdown-item" />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
